import { Button, Card, Col, notification, Row } from "antd"
import usePlatform from "contexts/platform/use-platform"
import { ICreatePolicyholder } from "platform-client/client/controllers/policyholders"
import { IPolicyholder, PolicyholderType } from "platform-client/types"
import React from "react"
import { useMutation } from "react-query"
import ClientForm, { IClientData } from "components/Client/ClientForm"
import ClientLookup from "components/Client/ClientLookup"
import ClientCard from "components/Client/ClientCard"
import styled from "styled-components"

const StyledButtonWrapper = styled.div`
  position: relative;
  display: flex;
  flex-wrap: nowrap;

  button {
    width: 100%;
  }
`

const CreateClient: React.FC<ICreateClientProps> = ({
  onBack,
  onSaveClient,
  showCreateForm = false,
  isLoading = false,
  ...props
}) => {
  const [showForm, setShowForm] = React.useState<boolean>(showCreateForm)
  const [client, setClient] = React.useState<IPolicyholder | undefined>(
    props.client
  )
  const platform = usePlatform()

  const createClient = useMutation((createClientRequest: ICreatePolicyholder) =>
    platform.policyholders.createPolicyholder(createClientRequest)
  )

  const onFinish = async (formData: IClientData) => {
    if (formData.policyholderType) {
      // Construct legalName from name fields for Individual, or use businessName for Business
      let legalName: string
      if (formData.policyholderType === "Business") {
        legalName = formData.businessName || ""
      } else {
        const nameParts = [
          formData.title,
          formData.firstName,
          formData.lastName
        ].filter(Boolean)
        legalName = nameParts.join(" ").trim()
      }

      if (!legalName) {
        notification.error({
          message: "Unable to submit form, missing required fields."
        })
        return
      }

      createClient
        .mutateAsync({
          businessName: formData.businessName || null,
          contact: {
            name: {
              firstName: formData.firstName || null,
              lastName: formData.lastName || null,
              title: formData.title || null
            },
            dateOfBirth: formData.dateOfBirth
              ? formData.dateOfBirth.format("YYYY-MM-DD")
              : null,
            phoneNumber: formData.phoneNumber || null,
            emailAddress: formData.emailAddress || null
          },
          contactAddress: {
            line1: formData?.contactAddress?.line1 || null,
            line2: formData?.contactAddress?.line2 || null,
            line3: formData?.contactAddress?.line3 || null,
            locality: formData?.contactAddress?.locality || null,
            province: formData?.contactAddress?.province || null,
            postalCode: formData?.contactAddress?.postalCode || null,
            country: formData?.contactAddress?.country
              ? JSON.parse(formData?.contactAddress?.country)
              : { referenceID: null, text: null },
            provinceReferenceID:
              formData?.contactAddress?.provinceReferenceID ?? null,
            uniqueReference: formData?.contactAddress?.uniqueReference ?? null
          },
          legalName: legalName,
          policyholderType: formData.policyholderType as PolicyholderType,
          ownerID: formData.ownerID || null,
          companyID: formData.companyID || null,
          referenceID: formData.referenceID || null
        })
        .then((response) => onSaveClient(response))
        .catch(() => {
          // Check response errors here and return relevant messages.
          notification.error({
            message: "Unable to create client, please try again."
          })
        })
    } else {
      notification.error({
        message: "Unable to submit form, missing required fields."
      })
    }
  }

  if (client && !showForm) {
    return (
      <Row>
        <Col span={10} offset={7} style={{ padding: "50px 0 " }}>
          <ClientCard
            client={client}
            onEdit={() => {
              setShowForm(true)
            }}
            onClear={() => {
              setClient(undefined)
            }}
          />
          <StyledButtonWrapper>
            {typeof onBack === "function" && (
              <Button
                style={{ marginRight: "15px" }}
                size="large"
                type="link"
                onClick={() => onBack()}
              >
                Go back
              </Button>
            )}
            <Button
              size="large"
              type="primary"
              onClick={() => onSaveClient(client)}
              loading={isLoading}
            >
              Save and continue
            </Button>
          </StyledButtonWrapper>
        </Col>
      </Row>
    )
  }

  if (!showForm) {
    return (
      <Row>
        <Col
          xs={{ span: 24, offset: 0 }}
          lg={{ span: 10, offset: 7 }}
          style={{ padding: "50px 0 " }}
        >
          <Card style={{ marginBottom: "15px" }}>
            <Card.Meta
              style={{ marginBottom: "15px" }}
              title={"Search Clients"}
              description="Use the field below to search policyholders, you can search by business name, legal name, firstname and lastname fields."
            />
            <ClientLookup onSelectClient={setClient} />
          </Card>

          <StyledButtonWrapper>
            {typeof onBack === "function" && (
              <Button
                style={{ marginRight: "15px" }}
                size="large"
                type="link"
                onClick={() => onBack()}
              >
                Go back
              </Button>
            )}
            <Button size="large" onClick={() => setShowForm(true)}>
              Create new client
            </Button>
          </StyledButtonWrapper>
        </Col>
      </Row>
    )
  }

  return (
    <ClientForm
      onCloseForm={() => setShowForm(false)}
      onFinish={onFinish}
      client={client}
      isLoading={createClient.isLoading}
    />
  )
}

interface ICreateClientProps {
  client?: IPolicyholder
  onSaveClient: (data: IPolicyholder) => void
  isLoading?: boolean
  onBack?: () => void
  showCreateForm?: boolean
}

export default CreateClient
